/**
 * theme/vault.ts — the ENCLOSED INTERIOR cache theme: a cut-stone vault.
 *
 * Where `ruin` is walled by terrain and open to the sky, a vault is a building: flat
 * floor slabs at per-room heights, masonry walls around every room perimeter with a
 * gap at each corridor mouth, enclosed halls between them, and A CEILING OVER
 * EVERYTHING. That last item is the only genuinely new problem in the theme, and it is
 * a camera problem, not a geometry one.
 *
 * ⚠ THE CAMERA PROBLEM (read before changing any height here). The third-person boom
 * pulls back and up; a low ceiling is exactly what it collides with, and `occludes()`
 * on the collider field is the lever that will pull the boom in. This dressing does
 * not wire the live camera (main.ts is not ours to touch yet) — but the boxes it emits
 * ARE what `occludes()` will read, so the contract is fixed here:
 *
 *   · every ceiling box is `standable: false` and sits ABOVE clear headroom —
 *     {@link VAULT_HEADROOM_MIN} over ordinary floors, {@link VAULT_ARENA_HEADROOM_MIN}
 *     over arenas and the boss room,
 *   · nothing overhead ever intrudes into the walk volume, which the render gate
 *     asserts by sweeping every corridor centreline and room diagonal at 2.5 m.
 *
 * Raise a ceiling freely; never lower one below those floors without re-running the
 * gate, because the failure mode (a boom that jams on a lintel) is invisible in a
 * screenshot and miserable in play.
 *
 * KIT DIMENSIONS, NOT KIT MESHES. The geometry is dimensioned off
 * `shared/src/homestead/pieces.ts` — {@link PIECE_UNIT_M} module, {@link PIECE_LEVEL_H}
 * storey, the 0.2 m wall panel (doubled here into 0.4 m masonry) and the 1.2 × 2.1 m
 * doorway — and the visual pass splits each merged wall into module-sized panels so it
 * reads as the kit. It does NOT instantiate the compiled piece assets: that path
 * (compileAsset → BuildingInstance → bakeBuildingMeshes) wants planet-local matrices
 * per piece and buys nothing at the silhouette grade this phase ships at.
 * TODO(P8): swap the wall/ceiling instancing for the real piece bake once the theme's
 * proportions are settled — the box dimensions are already the kit's.
 *
 * DETERMINISM. Everything derives from the layout plus `rngFromString(site:epoch:vault)`.
 */
import * as THREE from "three";
import {
  CACHE_PAD_RADIUS,
  CACHE_WALL_PARAMS,
  PIECE_LEVEL_H,
  PIECE_UNIT_M,
  rngFromString,
  type BoxColliderField,
  type CacheLayoutV1,
  type CachePropKind,
  type CacheRole,
} from "@hyperlight/shared";
import {
  addBoxInstances,
  addGroundMesh,
  addUnderplate,
  themeSink,
  addMarkers,
  addProps,
  addRoomFloors,
  boxRenderY,
  buildBoxField,
  buildGates,
  emitCorridorFlanks,
  emitRoomPerimeter,
  makeValueNoise,
  roomFloorHeights,
  smooth01,
  WalkField,
  type CacheDressing,
  type CacheGate,
  type PropStyle,
  type ThemeOptions,
  type VisualCtx,
  type WallBox,
} from "./common";

// ── knobs (binding — the render gate reads several of these) ───────────────────

/** Half-width of an enclosed hall, metres (an 8 m hall — wide enough to fight in).
 *  ⚠ ONE SOURCE OF TRUTH with the canonical set — see `CORRIDOR_HALF` in ruin.ts. */
export const VAULT_CORRIDOR_HALF = CACHE_WALL_PARAMS.vault.corridorHalf;
/** Masonry thickness, metres — the kit's 0.2 m wall panel, doubled. */
export const VAULT_WALL_T = 0.4;
/** Ceiling slab thickness, metres. */
export const VAULT_CEIL_T = 0.5;
/** Minimum clear headroom over an ordinary floor, metres. */
export const VAULT_HEADROOM_MIN = 3.6;
/** Minimum clear headroom over an arena / the boss room, metres. */
export const VAULT_ARENA_HEADROOM_MIN = 4.8;
/** Clear headroom in a hall, metres. */
export const VAULT_CORRIDOR_HEADROOM = 3.6;
/** Per-room ceiling variance above the minimum, metres. */
const HEADROOM_VARY = 0.7;
const ARENA_HEADROOM_VARY = 0.8;
/** How far a box sinks below its floor so a ramp never opens a gap under it. */
const SINK = 0.4;
/** Height ladder: `FLOOR_LADDER.vault` in shared/src/cache/layout.ts. */
/** Solid masonry fill above the highest floor, metres — the world outside the rooms. */
const FILL_H = 7.5;
/** Distance over which the ground climbs into that fill, metres. */
const FILL_RUN = 3.0;
/** Fill surface detail noise. */
const NOISE_AMP = 0.3;
const NOISE_SCALE = 9;
/** Visual-only reveal between wall panels, metres (the kit module read). */
const PANEL_REVEAL = 0.08;
/** Dynamic PointLights allowed in one vault — a perf cap, not a taste one. */
const MAX_POINT_LIGHTS = 8;

/** Floor tint per room role — cut stone, warm. */
const ROLE_COLOR: Record<CacheRole, number> = {
  entrance: 0x2f6f8e,
  transit: 0x6a6152,
  arena: 0x8a7550,
  gate: 0xb08a3a,
  key: 0x5c8f64,
  treasure: 0xc7ad55,
  boss: 0x93413b,
  shortcut: 0x6a5f8c,
  vista: 0x4d8a7c,
};

const PROP_STYLE: Record<CachePropKind, PropStyle> = {
  loot: { color: 0xe8c454, roughness: 0.3, metalness: 0.6 },
  light: { color: 0xfff0c8, emissive: 0xffc987, emissiveIntensity: 1.6 },
  cover: { color: 0x8b8271, roughness: 1 },
  hazard: { color: 0xc0452f, roughness: 0.8 },
  marker: { color: 0xe6ded0, roughness: 0.7 },
};

// ── public shape ──────────────────────────────────────────────────────────────

export interface VaultDressing extends CacheDressing {
  /** Overhead slabs. Every one is non-standable and above clear headroom. */
  readonly ceilings: readonly WallBox[];
  /** Clear headroom per room, metres (the ceiling base is `floorH[i] + headroom[i]`). */
  readonly headroom: readonly number[];
}

// ── build ─────────────────────────────────────────────────────────────────────

/**
 * Dress a layout as an enclosed vault.
 *
 * Returns the heightfield the pad host displaces its sphere with, the solid boxes the
 * character collides against (walls, ceilings and closed gates), and — unless
 * `visuals: false` — the render-space scene content. Pure in the layout + its seed.
 */
export function dressVault(layout: CacheLayoutV1, opts: ThemeOptions = {}): VaultDressing {
  const R = opts.radius ?? CACHE_PAD_RADIUS;
  const rng = rngFromString(`${layout.site}:${layout.epoch}:vault`);
  const rooms = layout.rooms;

  // ── 1. floors + per-room ceiling heights ────────────────────────────────────
  const floorH = roomFloorHeights(layout, rng, opts);
  // Live mode sinks every solid past the host planet's sampler relief (see themeSink).
  const sink = themeSink(SINK, opts);
  const headroom = rooms.map((r) => (
    r.role === "arena" || r.role === "boss"
      ? VAULT_ARENA_HEADROOM_MIN + rng() * ARENA_HEADROOM_VARY
      : VAULT_HEADROOM_MIN + rng() * HEADROOM_VARY
  ));
  let highest = 0;
  for (let i = 0; i < floorH.length; i++) highest = Math.max(highest, floorH[i]);
  const fillTop = highest + FILL_H;

  const walk = new WalkField(layout, floorH, VAULT_CORRIDOR_HALF, FILL_RUN + VAULT_WALL_T + 1);
  const noise = makeValueNoise(rng, NOISE_AMP, NOISE_SCALE);

  // ── 2. heightfield: flat floors inside, solid fill outside ──────────────────
  // Same nearest-feature law as `ruin`, with a much shorter run and no ridge noise
  // inside: past the wall line the world is masonry, so the ground simply becomes the
  // fill top. The walls are still the collision — this is the picture.
  const heightAt = (x: number, z: number): number => {
    const s = walk.sample(x, z);
    if (s.d === Infinity) return fillTop + noise(x, z);
    if (s.d <= 0) return s.h;
    const t = smooth01(s.d / FILL_RUN);
    return s.h + (fillTop - s.h) * t + noise(x, z) * t * t;
  };

  const sphereY = (x: number, z: number): number => {
    const rh = R + heightAt(x, z);
    return Math.sqrt(Math.max(0, rh * rh - x * x - z * z)) - R;
  };

  // ── 3. walls ────────────────────────────────────────────────────────────────
  // Room perimeters carry the room's own ceiling height so wall top == ceiling base;
  // halls carry the hall height. `allow` cuts any run that would cross open ground
  // (corridors may legally cross each other).
  const allow = (x: number, z: number): boolean => walk.nearestSd(x, z) > -0.15;
  const walls: WallBox[] = [];
  emitRoomPerimeter(walls, layout, walk, floorH, {
    wallT: VAULT_WALL_T,
    height: VAULT_HEADROOM_MIN,        // overridden per room by `heightOf`
    heightOf: (i) => headroom[i],      // wall top == that room's ceiling base
    sink,
    allow,
    corridorHalf: VAULT_CORRIDOR_HALF,
    chunk: 1.0,
  });
  emitCorridorFlanks(walls, walk, {
    wallT: VAULT_WALL_T,
    height: VAULT_CORRIDOR_HEADROOM,
    sink,
    allow,
    corridorHalf: VAULT_CORRIDOR_HALF,
    chunk: 1.0,
  });

  // ── 4. ceilings ─────────────────────────────────────────────────────────────
  const ceilings: WallBox[] = [];
  rooms.forEach((r, i) => {
    ceilings.push({
      cx: r.x, cz: r.z, along: "x",
      halfLen: r.w / 2 + VAULT_WALL_T,
      halfT: r.d / 2 + VAULT_WALL_T,
      baseH: floorH[i] + headroom[i],
      height: VAULT_CEIL_T,
    });
  });
  // Halls: chunked along the polyline at kit-module length, each chunk sitting on the
  // hall floor it spans (corridors ramp, so a single slab per corridor would either
  // scrape the high end or float over the low one).
  walk.corrs.forEach((c, ci) => {
    for (let k = 0; k + 3 < c.pts.length; k += 2) {
      const ax = c.pts[k], az = c.pts[k + 1], bx = c.pts[k + 2], bz = c.pts[k + 3];
      const len = Math.hypot(bx - ax, bz - az);
      if (len < 0.05) continue;
      const n = Math.max(1, Math.round(len / PIECE_UNIT_M));
      const alongX = Math.abs(bx - ax) > Math.abs(bz - az);
      for (let i = 0; i < n; i++) {
        const t = (i + 0.5) / n;
        const cx = ax + (bx - ax) * t, cz = az + (bz - az) * t;
        const seg = len / n;
        // Off the HIGHEST floor the slab spans, not its midpoint: halls ramp, and a
        // slab hung at the mid-height scrapes the top end of its own ramp.
        let top = -Infinity;
        for (let s = 0; s <= 4; s++) {
          const u = (i + s / 4) / n;
          top = Math.max(top, walk.corrFloorAt(ci, ax + (bx - ax) * u, az + (bz - az) * u));
        }
        const hl = seg / 2 + VAULT_WALL_T;
        const ht = VAULT_CORRIDOR_HALF + VAULT_WALL_T;
        const rx = alongX ? hl : ht, rz = alongX ? ht : hl;
        // ⚠ …AND OFF EVERY *OTHER* FEATURE THE SLAB COVERS. Corridors are allowed to
        // CROSS (space.ts only forbids routing through a third room), and two crossing
        // halls ramp to different heights either side of the shared cell — so a slab
        // hung off its own centreline alone overhangs the OTHER hall's ramp, and the
        // 2.5 m walk volume the camera boom needs is gone in a patch nobody can see in
        // a screenshot. Surfaced by the 2026-08-02 widening (halfT 2.9 → 4.4 reaches
        // 1.5 m further across the crossing) but the geometry was always wrong; the
        // measured worst case was ~5 cm of intrusion in 1 of 1666 swept legs.
        // `maxFloorOver` is the walk field's own answer to exactly this question.
        top = Math.max(top, walk.maxFloorOver(cx - rx, cx + rx, cz - rz, cz + rz, 4));
        ceilings.push({
          cx, cz, along: alongX ? "x" : "z",
          halfLen: hl,
          halfT: ht,
          baseH: top + VAULT_CORRIDOR_HEADROOM,
          height: VAULT_CEIL_T,
        });
      }
    }
  });

  // ── 5. gates ────────────────────────────────────────────────────────────────
  // A kit doorway is 1.2 × 2.1 in a 3 m panel; a vault gate is that door's slab scaled
  // to seal the whole hall — a 0.25 m plate spanning the opening, floor to ceiling.
  // TODO(P5): a corridor that CROSSES a gated one still bypasses the barrier
  // geometrically (space.ts only forbids routing through a third room) — the same
  // open item ruin.ts carries; it closes when the server owns progression.
  const gates: CacheGate[] = buildGates(layout, walk, {
    halfLen: VAULT_CORRIDOR_HALF + VAULT_WALL_T,
    halfT: 0.25,
    height: VAULT_CORRIDOR_HEADROOM,
    sink,
  });

  const buildField = (): BoxColliderField => buildBoxField(R, [walls, ceilings], gates);

  // ── 6. visuals ──────────────────────────────────────────────────────────────
  const group = new THREE.Group();
  group.name = "cacheVault";
  const disposables: { dispose(): void }[] = [];
  const gateMeshes = new Map<number, THREE.Object3D>();
  if (opts.visuals !== false) {
    buildVisuals(layout, group, disposables, gateMeshes, {
      sphereY, heightAt,
      keep: <T extends { dispose(): void }>(o: T): T => { disposables.push(o); return o; },
    }, walls, ceilings, gates, opts, R);
  }

  const dressing: VaultDressing = {
    layout,
    radius: R,
    heightAt,
    sphereY,
    colliders: buildField(),
    group,
    walls,
    ceilings,
    gates,
    floorH: Array.from(floorH),
    headroom,
    setGateOpen(edgeIndex: number, open: boolean): BoxColliderField {
      const g = gates.find((x) => x.edgeIndex === edgeIndex);
      if (g === undefined) throw new Error(`setGateOpen: edge ${edgeIndex} is not a gate`);
      g.open = open;
      const mesh = gateMeshes.get(edgeIndex);
      if (mesh) mesh.visible = !open;
      dressing.colliders = buildField();
      return dressing.colliders;
    },
    dispose(): void {
      for (const d of disposables) d.dispose();
      disposables.length = 0;
      group.clear();
    },
  };
  return dressing;
}

// ── visuals ───────────────────────────────────────────────────────────────────

/**
 * Split a merged wall box into kit-module panels with a thin reveal between them.
 * VISUAL ONLY — the collider keeps the merged box, so this cannot open a gap.
 */
function panelSplit(boxes: readonly WallBox[]): WallBox[] {
  const out: WallBox[] = [];
  for (const b of boxes) {
    const len = b.halfLen * 2;
    const n = Math.max(1, Math.round(len / PIECE_UNIT_M));
    const seg = len / n;
    for (let i = 0; i < n; i++) {
      const off = -b.halfLen + seg * (i + 0.5);
      out.push({
        ...b,
        cx: b.cx + (b.along === "x" ? off : 0),
        cz: b.cz + (b.along === "z" ? off : 0),
        halfLen: Math.max(0.05, (seg - PANEL_REVEAL) / 2),
      });
    }
  }
  return out;
}

function buildVisuals(
  layout: CacheLayoutV1,
  group: THREE.Group,
  _disposables: { dispose(): void }[],
  gateMeshes: Map<number, THREE.Object3D>,
  ctx: VisualCtx,
  walls: readonly WallBox[],
  ceilings: readonly WallBox[],
  gates: readonly CacheGate[],
  opts: ThemeOptions,
  radius: number,
): void {
  // Live mode: the host Planet already draws a ground — a second one interpenetrates
  // it — so the heightfield ground is replaced by a sunk backdrop plate.
  if (opts.flat === true) addUnderplate(group, ctx, layout, 0x6f6558, radius);
  else addGroundMesh(group, ctx, layout, 0x6f6558);
  addRoomFloors(group, ctx, layout, (role) => ROLE_COLOR[role]);

  addBoxInstances(group, ctx, panelSplit(walls), "cacheWalls", ctx.keep(
    new THREE.MeshStandardMaterial({ color: 0xa79c8a, roughness: 0.9, flatShading: true }),
  ));
  // Named so the lab (and any later cutaway view) can hide the lids in one call —
  // an enclosed theme is unreadable from a top-down debug camera otherwise.
  const ceilGroup = new THREE.Group();
  ceilGroup.name = "cacheCeilings";
  addBoxInstances(ceilGroup, ctx, ceilings, "cacheCeilingSlabs", ctx.keep(
    new THREE.MeshStandardMaterial({ color: 0x7c7263, roughness: 0.95, flatShading: true }),
  ));
  group.add(ceilGroup);

  {
    const geo = ctx.keep(new THREE.BoxGeometry(1, 1, 1));
    const mat = ctx.keep(new THREE.MeshStandardMaterial({ color: 0xd9a441, roughness: 0.45, metalness: 0.5 }));
    for (const g of gates) {
      const b = g.box;
      const mesh = new THREE.Mesh(geo, mat);
      mesh.scale.set(
        b.along === "x" ? b.halfLen * 2 : b.halfT * 2,
        b.height,
        b.along === "x" ? b.halfT * 2 : b.halfLen * 2,
      );
      mesh.position.set(b.cx, boxRenderY(ctx, b), b.cz);
      mesh.visible = !g.open;
      mesh.name = `cacheGate${g.edgeIndex}`;
      group.add(mesh);
      gateMeshes.set(g.edgeIndex, mesh);
    }
  }

  addProps(group, ctx, layout, PROP_STYLE, {
    color: 0xffce8a, intensity: 26, distance: 26, max: MAX_POINT_LIGHTS,
  });
  addMarkers(group, ctx, layout,
    { color: 0x5fd0f0, emissive: 0x1d6b86 },
    { color: 0x8e2f2f, emissive: 0x3a0c0c });
}

/** Role tint, exported for the lab's mission-graph overlay. */
export function vaultRoleColor(role: CacheRole): number {
  return ROLE_COLOR[role];
}

/** The kit dimensions this theme is built on, re-exported for the render gate. */
export const VAULT_KIT = { module: PIECE_UNIT_M, storey: PIECE_LEVEL_H } as const;
